import { SubSection } from "context/BlogContext";
import { useFormik } from "formik";
import React, { useState } from "react";
import {
  productInitialValues,
  productValidationSchema,
} from "./validationSchema";
import { FileEdit, Save, Trash2 } from "lucide-react";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import DeleteModal from "Common/DeleteModal";
import { getJewelleryBySlug } from "helpers/backend_helper";

interface ProductProps {
  sectionOrder: number;
  subSectionOrder: number;
  handleSaveSubSection: (
    sectionOrder: number,
    subSectionInfo: SubSection
  ) => void;
  handleEditSubSection: (sectionOrder: number, subSection: SubSection) => void;
  subSection: SubSection;
  handleDeleteSubSection: (subsectionOrder: number) => void;
}

const Product: React.FC<ProductProps> = ({
  sectionOrder,
  subSectionOrder,
  handleSaveSubSection,
  handleEditSubSection,
  subSection,
  handleDeleteSubSection,
}) => {
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [isCancel, setIsCancel] = useState<boolean>(false);

  const selectDataList = createSelector(
    (state: any) => state.Blog,
    (state) => ({
      isLoading: state.isLoading,
    })
  );

  const { isLoading } = useSelector(selectDataList);

  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: productInitialValues(subSection),
    validationSchema: productValidationSchema,
    onSubmit: (values) => {
      handleSaveSubSection(sectionOrder, {
        title: values?.title,
        type: "product",
        attrs: {
          id: values?.id,
          productTitle: values?.productTitle,
          image: values?.image,
          slug: values?.slug,
        },
        isEditing: false,
        order: subSectionOrder,
      });
    },
  });

  // Handle delete confirmation
  const handleDelete = () => {
    handleDeleteSubSection(subSection.order);
    setDeleteModal(false);
  };

  const handleCancelSubSection = () => {
    handleSaveSubSection(sectionOrder, {
      ...subSection,
      isEditing: true,
    });
    validation.resetForm();
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    validation.handleSubmit();
  };

  const jewelleryBySlug = async (slug: string) => {
    const removeMetalfromSlug = slug.replace(/-\d+k-[a-z]+$/i, "");
    const response: any = await getJewelleryBySlug(removeMetalfromSlug);
    const jewellery = response?.result;
    validation.setFieldValue("image", jewellery?.media[0]?.images[0] || "");
    validation.setFieldValue("productTitle", jewellery?.title || "");
    validation.setFieldValue("id", jewellery?._id || "");
  };

  return (
    <div>
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
        message="Are you sure you want to remove this subsection?"
      />
      {subSection.isEditing ? (
        <form>
          <div className="pt-5">
            <label
              htmlFor={`subTitle`}
              className="inline-block mb-2 text-base font-medium"
            >
              Title
            </label>
            <input
              type="text"
              id="title"
              className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
              placeholder="Title"
              name="title"
              onChange={validation.handleChange} // Bind Formik onChange
              value={validation.values.title} // Bind Formik value
            />
          </div>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12 pt-5">
            <div className="xl:col-span-6">
              <label
                htmlFor={`subTitle`}
                className="inline-block mb-2 text-base font-medium"
              >
                Slug
              </label>
              <input
                type="text"
                id="slug"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Slug"
                name="slug"
                onChange={validation.handleChange} // Bind Formik onChange
                value={validation.values.slug} // Bind Formik value
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault(); // optional: prevent form submit if inside a form
                    jewelleryBySlug(validation?.values?.slug);
                  }
                }}
              />
            </div>
            <div className="xl:col-span-6">
              <label
                htmlFor={`subTitle`}
                className="inline-block mb-2 text-base font-medium"
              >
                ImageUrl
              </label>
              <input
                type="text"
                id="imageUrl"
                className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                placeholder="Product image URL"
                name="imageUrl"
                onChange={validation.handleChange} // Bind Formik onChange
                value={validation?.values?.image} // Bind Formik value
                disabled
              />
            </div>
          </div>
        </form>
      ) : (
        <div className="max-w-6xl mx-auto bg-white rounded-lg overflow-hidden transition-shadow duration-300 flex flex-col md:flex-row">
          <img
            src={validation?.values?.image}
            alt="Product Image"
            className="w-full md:w-1/2 h-64 md:h-auto object-cover rounded-t-lg md:rounded-l-lg md:rounded-tr-none"
          />
          <div className="p-6 flex flex-col justify-center md:w-1/2">
            <h3 className="text-3xl font-bold text-gray-900 truncate">
              {validation?.values?.productTitle}
            </h3>
            <p className="mt-3 text-lg text-gray-700 select-all italic">
              {validation?.values?.slug}
            </p>
          </div>
        </div>
      )}
      {subSection?.isEditing && (
        <div className="flex gap-x-2 pt-5 justify-end">
          <button
            type="button"
            className={`text-custom-500 ${
              isLoading
                ? "cursor-not-allowed opacity-50"
                : "hover:bg-custom-200"
            } btn bg-custom-100 focus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
            onClick={() => jewelleryBySlug(validation?.values?.slug)}
            disabled={isLoading}
          >
            <div className="flex">
              <span>Get Product</span>
            </div>
          </button>
        </div>
      )}
      <div className="flex justify-end gap-x-2.5 pt-5">
        {subSection.isEditing ? (
          <div className="flex gap-x-3">
            {isCancel && (
              <button
                type="button"
                className={`${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } text-red-500`}
                onClick={() => handleCancelSubSection()}
                disabled={isLoading}
              >
                <div className="flex align-center">
                  <span className="ml-1">Cancel</span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`text-green-500 ${
                isLoading
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-green-600 hover:text-white"
              } bg-green-100 btn focus:text-white focus:bg-green-600 focus:ring focus:ring-green-100 active:text-white active:bg-green-600 active:ring active:ring-green-100 dark:bg-green-500/20 dark:text-green-400 dark:hover:bg-green-500 dark:hover:text-white dark:focus:bg-green-500 dark:focus:text-white dark:active:bg-green-500 dark:active:text-white dark:ring-green-400/20`}
              onClick={(e) => handleSubmit(e)}
              disabled={isLoading}
            >
              <div className="flex align-center">
                <Save className="size-4"></Save>
                <span className="ml-1">Save</span>
              </div>
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={`text-custom-500 ${
              isLoading
                ? "cursor-not-allowed opacity-50"
                : " hover:bg-custom-600 hover:text-white"
            } btn bg-custom-100 ocus:text-white focus:bg-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:ring active:ring-custom-100 dark:bg-custom-500/20 dark:text-custom-500 dark:hover:bg-custom-500 dark:hover:text-white dark:focus:bg-custom-500 dark:focus:text-white dark:active:bg-custom-500 dark:active:text-white dark:ring-custom-400/20`}
            onClick={() => {
              handleEditSubSection(sectionOrder, subSection);
              setIsCancel(true);
            }} // Switch to edit mode when clicked
            disabled={isLoading}
          >
            <div className="flex align-center">
              <FileEdit className="size-4"></FileEdit>
              <span className="ml-1">Edit</span>
            </div>
          </button>
        )}
        <button
          type="button"
          className={`${
            isLoading ? "cursor-not-allowed opacity-50" : ""
          } text-red-500`}
          onClick={() => setDeleteModal(true)}
          disabled={isLoading}
        >
          <div className="flex align-center">
            <Trash2 className="size-4 text-red-500"></Trash2>
            <span className="ml-1">Delete</span>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Product;
